import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryState } from './QueryState';
import { HABIT_PERIOD_NOUN, habitPeriodsCompleted } from '../taskVocab';
import { readableDay } from '../dateLabels';
import { shiftDay, todayInZone } from '../timezones';
import { periodTone, runFigure, runWindowSpan } from '../habitVocab';
import {
  getHabitHistory,
  listHabitEntries,
  logHabit,
  updateHabit,
  deleteHabitEntry,
  ApiError,
  type HabitProgress,
  type HabitPeriod,
  type HabitPeriodResult,
  type HabitHistory as HabitHistoryData,
  type HabitTrend,
} from '../api';

/**
 * How often the target was actually met, and the entries behind it.
 *
 * §5.3 chose frequency targets over streaks so "3× a week" survives a missed Tuesday. The
 * consequence never built was the measure that choice implies: the screen showed the current
 * period's ratio and a year of undifferentiated dots, neither of which says whether this is a
 * habit or a run of good intentions. All three mockup directions specify this independently —
 * "Weeks at target" and "Last 12 weeks".
 */

/** "17 Aug" — the family's `day`, which prints a year only for another year's period. */
export const shortDay = (iso: string) => readableDay(iso);

/*
  A fraction's denominator is visible text; provenance stays a tooltip.

  `hint` is a `title` — right for a fact that *adds* to a number ("Reached on 17 Aug" under "Best
  82 kg"), and wrong for the thing that makes a number checkable. "Days at target 20/28" over a
  chart drawing ninety days needs the 28 explained where the reader is looking, and a tooltip is
  unreachable on touch entirely.

  `sub` is the same prop `GoalDetail`'s tile has had since its Open/Done row, rendered with the same
  `.stat-sub` class — "smaller than the label, because it qualifies the number rather than naming
  it". A tile taller than its neighbours is that row's existing shape, not a new cost.
*/
function Tile({
  label,
  value,
  hint,
  sub,
  testId,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  /** The denominator, said out loud. Only for a tile whose value is a fraction. */
  sub?: string;
  testId?: string;
  /*
    `ok` or `bad`, for the one tile whose number is good or bad news rather than just a number.

    The tone only reinforces: every tile that has one also says the word — see `trendTile`. A
    reader who cannot separate the two greens still reads "better".
  */
  tone?: string;
}) {
  const toneClass = tone ? `tone-${tone}` : '';
  return (
    <div className={`stat-tile ${toneClass}`} data-testid={testId} title={hint}>
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
      {sub !== undefined && <span className="stat-sub">{sub}</span>}
    </div>
  );
}

/**
 * Headroom above the target, so the target line means something.
 *
 * Scaling to `max(target, biggest period)` put the line at 100% of the box whenever nothing
 * had exceeded the target — which is most habits most of the time — so it drew along the top
 * edge and conveyed nothing. A quarter of headroom puts it at 80%: a period that met the
 * target visibly reaches the line, and one that beat it visibly passes it.
 */
export const HEADROOM = 1.25;

export function barScale(target: number, totals: readonly number[]): number {
  return Math.max(target * HEADROOM, ...totals, 1);
}

/** A tenth of the observed range, above and below, so the extreme bars are not flush. */
const BAND_PADDING = 0.1;

export interface BarBand {
  /** The value the bars grow from. Zero for anything you accumulate. */
  floor: number;
  /** The value at the top of the box. */
  peak: number;
}

/**
 * The vertical extent of the chart.
 *
 * A quantity you accumulate grows from nothing, so zero is where its bars start and the old
 * scale is right. A measurement does not: seventy body weights between 78.84 and 82.0 kg drew
 * bars spanning 80.9% to 84.1% of the box — a flat grey slab, with three kilos of real movement
 * compressed into three pixels, because 96% of every bar was the distance from zero to a weight
 * nobody has ever been.
 *
 * So a measurement is scaled to what was observed, padded a little at both ends and stretched to
 * include the target so the target line stays on the chart.
 */
export function barBand(target: number, totals: readonly number[], measurement: boolean): BarBand {
  if (!measurement) return { floor: 0, peak: barScale(target, totals) };

  // Empty periods are absences, not readings of zero: a fortnight without weighing must not drag
  // the floor to the origin and undo the whole point of this.
  const readings = totals.filter((t) => t > 0);
  if (readings.length === 0) return { floor: 0, peak: barScale(target, totals) };

  const low = Math.min(...readings, target);
  const high = Math.max(...readings, target);
  const range = high - low;
  /*
    Padding proportional to the range, not to the values.

    A percentage of the reading itself looks equivalent and is not: 1% of 82 kg is 0.82, which
    against a four-kilo range is a fifth of the chart given away at each end for nothing. The
    only case that needs an absolute floor is a range of exactly zero — every reading identical,
    where the alternative is dividing by nothing.
  */
  const pad = range === 0 ? Math.max(Math.abs(high) * 0.01, 1) : range * BAND_PADDING;
  return { floor: low - pad, peak: high + pad };
}

/** Where a value sits in the band, 0–1. */
export const bandFraction = (band: BarBand, value: number): number =>
  Math.max(0, Math.min(1, (value - band.floor) / Math.max(band.peak - band.floor, 1e-9)));

/**
 * One bar per period, oldest on the left.
 *
 * Height is the total against the target, so a bar reaching the line met it. This replaces
 * nothing — the year heatmap still shows *when* — but it is the view that answers the
 * question the target poses, which a dot grid cannot.
 */
function PeriodBars({
  periods,
  target,
  unit,
  periodName,
  measurement,
  polarity,
}: {
  periods: HabitPeriodResult[];
  target: number;
  unit: string | null;
  periodName: string;
  /** For the hover text, which says "of 3 allowed" on a ceiling as the strip does. */
  polarity: 'positive' | 'negative';
  /** A reading rather than a total: the bars are scaled to the data, not to zero. */
  measurement: boolean;
}) {
  const band = barBand(
    target,
    periods.map((p) => p.total),
    measurement,
  );

  return (
    <div className="period-bars" data-testid="period-bars">
      {periods.map((p) => {
        const height = Math.round(bandFraction(band, p.total) * 100);
        /*
          Nothing logged draws nothing — on a habit you are trying to *do*. A one-pixel sliver
          there reads as a trace of activity, which is the opposite of what an empty period means.

          On a habit you are trying *not* to do, the same zero is the best possible result. A
          habit whose tile read "8 of 11 weeks at target" drew six bars, because the six perfect
          weeks logged nothing and so drew nothing: the chart of the strongest habit on the
          account showed only its failures. Zero-and-met gets a baseline tick, zero-and-missed
          still gets nothing.
        */
        /*
          And nothing measured draws nothing either, in its own tone.

          This chart had exactly three answers — running, met, missed — so a day nobody took a
          reading on had to be one of them. It was `met` until Phase 51 ("0 — met" over fourteen
          solid cells, on a habit last measured a fortnight earlier) and `missed` after, which is
          the same fabricated zero read the other way: the six mornings actually weighed drew a
          verdict and the eight skipped drew a louder one. An absence is neither, and the cell now
          says so instead of picking a side.
        */
        /*
          And a period from before the habit existed is not a result of any kind.

          The chart draws ninety days whatever the habit's age, so a habit started thirty days ago
          gets sixty bars for days nobody was trying anything — and they were being scored. On a
          floor they read "0 of 20 — missed", sixty verdicts on a habit that did not exist; on a
          ceiling the same zero is under the limit, so they read "— met" and drew the baseline tick
          that means a period kept. Both are the fabricated zero this chart keeps meeting, and both
          are answered here rather than in the verdict below, because there is no verdict to pick.

          `applicable` is the domain's own answer and has been on the response since `b08cea0`;
          `trackedSince` was its only reader. `unmeasured` is deliberately *not* folded into it —
          `computeHabitHistory` marks both kinds inapplicable, and "nobody took a reading" is a
          different sentence from "there was no habit yet", which is the distinction `f15de25`
          existed to draw.

          `paused` is the third of those sentences and it used to be swept into this one. Pause
          writes `activeTo` as yesterday, so every bar from the pause onwards — today's included,
          the one under the hatch — announced that a habit of several months had not started yet.
          Drawing nothing was already right for them; the card's own pause copy says paused
          periods are out of the rate and out of both runs. Only the words needed splitting, and
          the domain says which reason it is rather than the bar comparing dates against a habit
          it is deliberately not given.
        */
        const beforeItExisted = !p.applicable && !p.unmeasured && !p.paused;
        /*
          The two of them together, because everything except the sentence treats them alike: no
          habit was running in this period, so there is no verdict to draw and no fill to draw it
          with. Splitting `paused` out of `beforeItExisted` without this would have handed the
          paused bars back to the `met`/`missed` branch below — the "0 of 20 — missed" this chart
          has been climbing out of since Phase 51, reintroduced by a change about wording.
        */
        const notRunning = beforeItExisted || p.paused;
        const empty = notRunning || (p.total === 0 && !p.met) || p.unmeasured;
        const zeroButMet = p.total === 0 && p.met && !p.unmeasured && !notRunning;
        /*
          `isCurrent` still wins, because "still running" is not a verdict either — it is the
          hatch that says which bar is now, and a period in progress that has not been measured
          *yet* is the one case where the absence really might still be filled in.

          A period can be current *and* before the habit started only when `activeFrom` is in the
          future, and there the hatch is still the right mark for "this is the one you are on" —
          the title is what has to say the habit has not started.
        */
        /* The same derivation the row strip uses, so the strip above this chart cannot paint a
           week green that this chart paints orange — see `periodTone`. */
        const tone = periodTone(p);
        return (
          <div
            key={p.bounds.start}
            className={`period-bar tone-${tone}`}
            data-testid="period-bar"
            data-period={p.bounds.start}
            data-met={p.met}
            data-current={p.isCurrent}
            /* "78.84 kg of 78 — missed" describes a body weight as a workload half-finished.
               A reading is stated against the target, not divided by it.

               And an unmeasured period states no figure at all. Printing "0 of 50" there is where
               the reported bug actually lives: the zero is invented, and once it is on screen the
               word after it is only ever a choice of which way to be wrong about it. */
            title={
              beforeItExisted
                ? `${periodName} of ${shortDay(p.bounds.start)}: before this habit started`
                : p.paused
                  ? `${periodName} of ${shortDay(p.bounds.start)}: paused`
                  : p.unmeasured
                    ? `${periodName} of ${shortDay(p.bounds.start)}: not measured`
                    : `${periodName} of ${shortDay(p.bounds.start)}: ${periodFigure(
                        p.total,
                        target,
                        unit,
                        polarity,
                        measurement,
                      )}${p.isCurrent ? ' — still running' : p.met ? ' — met' : ' — missed'}`
            }
          >
            {!empty && (
              <span
                className={zeroButMet ? 'period-bar-fill is-clean' : 'period-bar-fill'}
                style={zeroButMet ? undefined : { height: `${height}%` }}
              />
            )}
          </div>
        );
      })}
      {/* Where the target sits, so "did it reach" is answerable by eye rather than by
          reading twelve tooltips — and what the line *is*, so the answer does not require
          remembering the number. */}
      <span
        className="period-target-line"
        style={{ bottom: `${Math.round(bandFraction(band, target) * 100)}%` }}
        aria-hidden="true"
      >
        <span className="period-target-label">{amount(target, unit)}</span>
      </span>
      {/*
        The ends of the axis. Twelve bars with no dates under them say "some period" — the first
        and the last are enough to place the rest, without twelve labels fighting for the width.
      */}
      {periods.length > 1 && (
        <span className="period-axis" aria-hidden="true">
          <span>{shortDay(periods[0]!.bounds.start)}</span>
          <span>{shortDay(periods[periods.length - 1]!.bounds.start)}</span>
        </span>
      )}
    </div>
  );
}

/** The log itself, so the record can be checked and corrected. */
function Entries({ habitId, from, to }: { habitId: string; from: string; to: string }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const entries = useQuery({
    queryKey: ['habit-entries', habitId, from, to],
    queryFn: () => listHabitEntries(habitId, from, to),
  });

  const remove = useMutation({
    mutationFn: (entryId: string) => deleteHabitEntry(habitId, entryId),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not remove that.'),
  });

  if (entries.isPending || entries.isError) return <QueryState query={entries} />;
  // Only once it is known to be empty. `data ?? []` reported a failed fetch as "nothing
  // logged", which for a habit screen is the single most discouraging thing it could say
  // wrongly.
  if (entries.data && entries.data.length === 0) {
    return <p className="muted">Nothing logged in this period.</p>;
  }

  return (
    <>
      <ul className="entry-list" data-testid="entry-list">
        {entries.data?.map((entry) => (
          <li key={entry.id} data-testid="habit-entry">
            <span className="entry-day">{shortDay(entry.occurredOn)}</span>
            <span className="entry-value">{entry.value}</span>
            {entry.note && <span className="subtext">{entry.note}</span>}
            {/* An automatic entry belongs to a completed task. Removing it here would leave
                the task claiming credit for something no longer recorded, so it is shown and
                explained rather than offered for deletion. */}
            {entry.source === 'auto' ? (
              <span className="pill" title="Logged by completing a task">
                from a task
              </span>
            ) : (
              <button
                className="link danger"
                aria-label={`Remove the entry of ${entry.value} on ${entry.occurredOn}`}
                onClick={() => remove.mutate(entry.id)}
              >
                remove
              </button>
            )}
          </li>
        ))}
      </ul>
      {error && <p className="error">{error}</p>}
    </>
  );
}

/**
 * Whether backdating is safe to submit.
 *
 * The amount defaults to 1 because an occurrence is one occurrence, and for a `latest` habit
 * that default is a trap: pick a date, press the button, and **1 kg** becomes that morning's
 * reading — not one kilogram added to it, the whole reading — which then feeds the trend, the
 * band, the best-ever and the chart. So a measurement must be given a number before the button
 * does anything.
 */
export const canBackdate = (measurement: boolean, day: string, value: string): boolean => {
  if (day === '') return false;
  if (!measurement) return true;
  const parsed = Number(value);
  return value.trim() !== '' && Number.isFinite(parsed) && parsed >= 0;
};

/**
 * The amount a fresh backdate field starts on.
 *
 * `typicalValue` is what the row's own quick-log offers — the amount you usually record, from the
 * last few entries. This field offered the literal number **1** on every habit, so setting a date
 * on Meditation (typical 20 minutes) and pressing the loud black "Log it" recorded *one minute*.
 * Deep sleep, typical 84. HN Steps, typical 18,013.
 *
 * `LogHabit.tsx`'s own header comment calls this exact failure unacceptable on the row — "pressing
 * +1 on a habit measured in kilometres recorded one kilometre" — and the backfill form was written
 * beside it without the fix.
 *
 * A *reading* still starts blank: the app has no idea what you weighed on a Tuesday in June, and
 * offering the last figure there would be a guess presented as a record. `1` remains the fallback
 * for an occurrence habit with no history to draw a typical amount from.
 */
export const backdateDefault = (measurement: boolean, typicalValue?: number | null): string =>
  measurement ? '' : String(typicalValue ?? 1);

/**
 * Logging a session you forgot at the time.
 *
 * The confirmation is not decoration. The entries list below covers the *current* period, so
 * backdating into an earlier one succeeds and then shows nothing — press "Log" for
 * yesterday on a Monday and the screen was silent, which is indistinguishable from a failure.
 * Naming the day, and saying when it fell outside this period, is the difference.
 */
function Backdate({
  habitId,
  unit,
  bounds,
  periodName,
  measurement,
  typicalValue,
  timezone,
}: {
  habitId: string;
  unit: string | null;
  bounds: { start: string; end: string };
  periodName: string;
  /** A reading rather than an occurrence: there is no sensible default for one. */
  measurement: boolean;
  /** The amount you usually log — see `backdateDefault`. */
  typicalValue: number | null;
  /**
   * The account's zone, so the picker's ceiling — and the day *Yesterday* means — is the day the
   * account is on.
   *
   * `max` was `new Date().toISOString().slice(0, 10)` — the UTC day. Ahead of UTC that put today
   * *below* the ceiling and the user could not pick it, while the row's own +1 logged it happily;
   * behind UTC it offered tomorrow, which `logHabitEntries` has refused with `validation_failed`
   * since `51cd9e7`. A form is not allowed to put a day on the menu the server will reject.
   */
  timezone: string;
}) {
  const queryClient = useQueryClient();
  const [day, setDay] = useState('');
  // Blank for a reading, because the app has no idea what you weighed on a Tuesday in June.
  const [value, setValue] = useState(backdateDefault(measurement, typicalValue));
  const [error, setError] = useState<string | null>(null);
  const [logged, setLogged] = useState<{ day: string; value: number } | null>(null);

  const parsed = Number(value);
  const amountToLog = value.trim() !== '' && Number.isFinite(parsed) ? parsed : 1;

  const log = useMutation({
    mutationFn: () => logHabit(habitId, { occurredOn: day, value: amountToLog }),
    onSuccess: () => {
      setError(null);
      setLogged({ day, value: amountToLog });
      setDay('');
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      setLogged(null);
      setError(e instanceof ApiError ? e.message : 'Could not log that.');
    },
  });

  const outsidePeriod = logged !== null && (logged.day < bounds.start || logged.day > bounds.end);

  const today = todayInZone(timezone);
  /*
    The two days somebody backfilling actually means, as buttons, with the picker kept for the rest.

    Derived from `todayInZone(timezone)` like `max` below and for the same reason, never from
    `new Date(Date.now() - 86_400_000)`: that is the browser's or UTC's yesterday, which is another
    day than the account's for hours of every day anywhere far from Greenwich — an Auckland account
    on a Honolulu laptop pressed "Yesterday" and would have logged the day before it.
  */
  const presets = [
    { label: 'Yesterday', day: shiftDay(today, -1) },
    { label: '2 days ago', day: shiftDay(today, -2) },
  ];

  return (
    <div className="row gap backdate">
      <span className="subtext">{measurement ? 'Add a past reading?' : 'Missed a day?'}</span>
      {presets.map((p) => (
        <button
          key={p.label}
          type="button"
          className="secondary"
          aria-pressed={day === p.day}
          onClick={() => setDay(p.day)}
        >
          {p.label}
        </button>
      ))}
      {/* The native field's display format is the browser's locale, not ours, so it reads
          `mm/dd/yyyy` on an en-US browser beside a page that writes "7 Sept". It cannot be restyled;
          what changed is that the two common days no longer need it, and the confirmation below
          names the day in the app's own form. */}
      <label className="subtext">
        or pick a date
        <input
          type="date"
          value={day}
          aria-label="Log a past day"
          max={today}
          onChange={(e) => setDay(e.target.value)}
        />
      </label>
      <input
        type="number"
        min={0}
        step="any"
        className="habit-amount"
        value={value}
        placeholder={measurement ? (unit ?? '') : ''}
        aria-label={
          measurement ? `Reading${unit ? ` in ${unit}` : ''}` : `Amount${unit ? ` in ${unit}` : ''}`
        }
        onChange={(e) => setValue(e.target.value)}
      />
      {/* Named beside the field, not only in a label a screen reader gets: "20" on its own does not
          say minutes, and this form is where a wrong unit becomes a wrong record. */}
      {unit && <span className="subtext backdate-unit">{unit}</span>}
      <button
        type="button"
        disabled={!canBackdate(measurement, day, value) || log.isPending}
        onClick={() => log.mutate()}
      >
        {/* The row's verb. This said "Record it" or "Log it" for the act the row calls "Log". */}
        Log
      </button>
      {logged && (
        <p className="subtext" data-testid="backdate-confirmation">
          Logged {logged.value}
          {unit ? ` ${unit}` : ''} on {shortDay(logged.day)}
          {outsidePeriod
            ? ` — an earlier ${periodName}, so it is in the bars above rather than the list below.`
            : '.'}
        </p>
      )}
      {error && <p className="error subtext">{error}</p>}
      {/* The other door. Today's previous-day arrow shows every row with its own quick-log for
          that day, which is the better path for more than one habit, and nothing said so. */}
      <p className="subtext backdate-hint">or step Today back a day — every row logs there.</p>
    </div>
  );
}

/**
 * The fix, next to the finding.
 *
 * The week review already says "worth changing the target rather than trying harder". Without a
 * number that is advice you cannot act on, and changing a target by hand is five clicks through a
 * screen you are not on.
 *
 * The framing is deliberate. The same numbers read as "you are failing" or as "your target is
 * wrong", and only the second leads anywhere — so the sentence names what actually happened and
 * proposes a number, rather than reporting a shortfall.
 */
function TargetSuggestionRow({
  habitId,
  suggestion,
  current,
  unit,
  period,
}: {
  habitId: string;
  suggestion: NonNullable<HabitHistoryData['suggestion']>;
  current: number;
  unit: string | null;
  period: string;
}) {
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState(false);

  const suggested = suggestion.suggested;
  const apply = useMutation({
    // Only reachable with a number: the no-number box below renders no button to call it.
    mutationFn: () => updateHabit(habitId, { targetCount: suggested ?? current }),
    onSuccess: () => void queryClient.invalidateQueries(),
  });

  if (dismissed) return null;

  /*
    Kept earlier, then nothing — over weeks the account itself was barely here.

    The domain sends no number for this (`suggested: null`), because there is no recent record to
    size one from: Deep sleep, 90 minutes a night through 18 Aug, was offered "a target of 1 minute
    per day" with a one-tap *Use 1*. So this says what happened and offers nothing to apply — a
    button here would rewrite a deliberate target from an absence.
  */
  if (suggested === null) {
    return (
      <div className="banner target-suggestion" data-testid="target-suggestion">
        <p>
          Nothing logged here
          {suggestion.since ? ` between ${readableDay(suggestion.since)} and ` : ' before '}
          {period === 'day' ? 'today' : `this ${period}`}, and too little else logged in that time
          to say what you typically manage — so no target to suggest. Pick it back up at{' '}
          {amount(current, unit)}, or archive it; both are answers.
        </p>
      </div>
    );
  }

  return (
    /*
      A box holding a sentence and a row of buttons, not a sentence with buttons inside it.

      `Use 14` and `keep 12` were `.link`s one space after the prose, and they measured 49×30 and
      56×30 seven pixels apart on a phone — under the 44px thumb floor and close enough together to
      hit the wrong one. The floor rule exempts `.link` on purpose, because a link *is* a word in a
      sentence, so the fix is not to pad the link: it is to stop these two being words.

      As plain buttons in a `.row.gap` they inherit the generic floor at ≤40rem with no new CSS,
      which is the check that the floor rule has no gap in it — if this had needed a rule of its
      own, that would have been the finding.

      The element becomes a `div` because a `p` cannot contain one, and the box is what keeps the
      buttons attached to the sentence that explains them.
    */
    <div className="banner target-suggestion" data-testid="target-suggestion">
      <p>
        {/*
        One sentence per situation.

        There were three sentences for four situations, and the two that shared one mean opposite
        things: raising a floor you always beat, and raising a ceiling you always breach. A user
        who had met a body-fat ceiling in zero of eleven days was told "Met every one of the last
        11 days", with a one-click button to raise the ceiling. The domain now names the
        situation, so the copy cannot be shared by accident again.

        The fourth sentence has since gone with the situation: nothing offers to raise a ceiling
        because it is being broken, so there is no "Over this ceiling in N of the last M days"
        copy to write. See `suggestTarget`.

        Every number goes through `amount()`, which is where the unit is spelled and rounded. This
        pasted `{value}{units}` instead and got both wrong on the same habit: "typically **1
        sessions**. A target of **1 sessions** per week would be one you hit" — the plural twice in
        the one sentence written to be checked — and `typical` is a median, so an unrounded half of
        two floats could print itself in full here while every tile beside it rounded.
      */}
        {suggestion.rationale === 'missing' && suggestion.basis === 'restart' ? (
          <>
            Nothing logged in {suggestion.periodsConsidered} {period}s — this target is not being
            attempted rather than narrowly missed. {amount(suggested, unit)} per {period} is a place
            to start again from; archiving it is also an answer.
          </>
        ) : suggestion.rationale === 'missing' ? (
          <>
            Met in {suggestion.periodsMet} of the last {suggestion.periodsConsidered} {period}s,
            typically {amount(suggestion.typical, unit)}. A target of {amount(suggested, unit)} per{' '}
            {period} would be one you hit.
          </>
        ) : suggestion.rationale === 'outgrown' ? (
          <>
            Met every one of the last {suggestion.periodsConsidered} {period}s, typically{' '}
            {amount(suggestion.typical, unit)} against a target of {amount(current, unit)}.{' '}
            {amount(suggested, unit)} would describe what you actually do now.
          </>
        ) : (
          <>
            Under this ceiling every one of the last {suggestion.periodsConsidered} {period}s,
            typically {amount(suggestion.typical, unit)} against a limit of {amount(current, unit)}.{' '}
            {amount(suggested, unit)} would still be comfortable.
          </>
        )}
      </p>
      {/* Plain `.row`, not `.row.gap`: the `gap` modifier *narrows* the spacing to 0.4rem, and two
          adjacent thumb targets measured 6px apart with it — the finding's own "7px apart" again,
          with the heights fixed and the aim still wrong. `.row`'s 0.5rem is the floor these want. */}
      <div className="row">
        <button type="button" disabled={apply.isPending} onClick={() => apply.mutate()}>
          Use {suggested}
        </button>
        <button type="button" onClick={() => setDismissed(true)}>
          keep {current}
        </button>
      </div>
    </div>
  );
}

/**
 * `of the last 11 completed weeks · tracked since 25 May` — the denominator's window, then the
 * record's own start.
 *
 * Two facts, and the tile used to run them together into a third that was false. It read
 * `of 11 weeks tracked since 22 Jun`, where the date came from the first *applicable* period of
 * the window being drawn — so for any habit older than the chart it was the chart's left edge
 * wearing the words "tracked since". Barbell tonnage, first logged on 25 May, was described as
 * tracked since 22 Jun beside an All time tile holding 215.44 t of which the twelve bars showed
 * 155.74; ask the same habit for 52 periods and the sentence said 25 May instead. A date that
 * moves when you widen a chart is not a fact about the habit.
 *
 * So the start comes off the payload (`trackedSince`, derived from the record) and the count says
 * which periods it counted. "the last N completed <periods>" is the review callout's phrasing for
 * the same quantity and for the same reason — a bare denominator reads as a claim about the whole
 * record, which here is precisely the claim that was wrong. See `habitPeriodsCompleted`.
 *
 * The other branch — widening the rate to the whole record and leaving the chart at twelve — was
 * rejected: it puts a second, larger denominator on a panel whose complaint was already that its
 * numbers cover different stretches, and the chart would still be the only unlabelled one.
 */
interface HistorySentence {
  periodsConsidered: number;
  trackedSince: string;
  /**
   * Read only for the periods left out of the denominator. Optional because a caller that has no
   * period list is asking about a habit that has never been paused and never gone unrecorded, and
   * the clause below simply does not appear.
   */
  periods?: readonly {
    bounds: { end: string };
    paused: boolean;
    unmeasured: boolean;
    isCurrent: boolean;
  }[];
}

/**
 * The completed periods that are **not** in the denominator, and which reason each one is out for.
 *
 * Two reasons, one derivation, because they end up in two sentences on the same row — the met
 * tile's and the run tile's — and this row's whole history is numbers that covered different
 * stretches without saying so.
 *
 * `bounds.end >= trackedSince` is the guard that keeps "before this habit existed" out of the
 * unrecorded count. A ceiling you *read* marks every period with no reading `unmeasured`, the
 * ones from before the habit was created included, so without it a blood-pressure habit made a
 * month ago would report sixty days "with nothing recorded" on a ninety-day chart. The date is
 * the one this sentence already prints, and it is `activeSince` — the same value the domain
 * judges with, so the words and the counts cannot come from two windows.
 */
const notCountedIn = (history: HistorySentence): { paused: number; unrecorded: number } => {
  const dropped = (history.periods ?? []).filter(
    (p) => !p.isCurrent && p.bounds.end >= history.trackedSince,
  );
  return {
    paused: dropped.filter((p) => p.paused).length,
    // A paused period is never also asked the evidence question — see `itsToKeep` in
    // `habitHistory.ts` — but a measurement's blank ones can be both, and one period must not be
    // subtracted twice in a sentence a reader is expected to check by hand.
    unrecorded: dropped.filter((p) => p.unmeasured && !p.paused).length,
  };
};

export const metSub = (history: HistorySentence, period: string): string => {
  /*
    And which completed periods are *not* in the denominator.

    The count already excludes them — a paused period is inapplicable, so it is out of
    `periodsConsidered` — and that was the half nothing on screen said. "of the last 11 completed
    weeks" over a habit paused for a month names a window whose last four weeks are not in it,
    which reads as an arithmetic slip rather than as the pause working. The clause is subordinate
    on purpose: round 4 settled the denominator's own phrasing and this qualifies it rather than
    offering a second one.

    Round 6 added the paused half; this adds the unrecorded one, and they share a clause on
    purpose. A denominator that excluded paused periods but silently kept the days nobody was here
    for would be two rules for one number — which is the thing this phase is named after.
  */
  const { paused, unrecorded } = notCountedIn(history);
  const excluded = [
    ...(unrecorded === 0 ? [] : [`${unrecorded} with nothing recorded`]),
    ...(paused === 0 ? [] : [`${paused} paused`]),
  ];
  return (
    `of the last ${habitPeriodsCompleted(history.periodsConsidered, period)}` +
    (excluded.length === 0 ? '' : `, not counting ${excluded.join(' and ')}`) +
    ` · tracked since ${readableDay(history.trackedSince)}`
  );
};

/**
 * The run tile's tooltip: the window both runs were counted over, and what a gap in it did.
 *
 * The window half is round 4's — a best run that silently forgets everything left of the chart is
 * the number on this row nobody would think to doubt. The second half is this round's, and it is
 * a decision rather than a description: a period nobody recorded on is **removed** from the
 * sequence, so the run closes over the gap rather than being broken by it.
 *
 * Said out loud because the alternative's whole failure mode is doing it *silently*. Breaking the
 * run instead would assert that the habit was broken on a day the app has no evidence about,
 * which is the fabrication this phase removes, signed the other way; bridging without saying so
 * would let a reader count a holiday as part of a streak. See the note above `forRuns` in
 * `habitHistory.ts`, which is where the decision lives.
 */
export const runHint = (
  history: HistorySentence,
  periodsDrawn: number,
  period: HabitPeriod,
): string => {
  const { unrecorded } = notCountedIn(history);
  // The window in `runWindowSpan`'s words, which the row's *best 4 in 90 days* is said in too.
  return (
    `Longest run of ${period}s at target in the last ${runWindowSpan(periodsDrawn, period)}` +
    (unrecorded === 0
      ? ''
      : ` · ${unrecorded} with nothing recorded, which neither extend a run nor break it`)
  );
};

/**
 * One of whatever the unit is called, when there is exactly one of them.
 *
 * The unit is free text the user typed — "sessions", "pages", "glasses", "km" — and it is stored
 * in the plural because that is how you name a thing you do several of a week. Every count of one
 * then read wrong: "typically **1 sessions**. A target of **1 sessions** per week would be one you
 * hit" put it twice in a sentence whose whole job is to be checked by the reader.
 *
 * English's `-es` cases are the reason this is not `replace(/s$/, '')`: that turns "glasses" into
 * "glasse". A unit with no trailing `s` — "km", "kg", "t", "mmHg" — is left exactly as typed,
 * which is most of them, so this can only ever help.
 */
const singular = (unit: string): string =>
  /(?:ss|x|z|ch|sh)es$/i.test(unit)
    ? unit.slice(0, -2)
    : /[^s]s$/i.test(unit)
      ? unit.slice(0, -1)
      : unit;

/**
 * A number a person would write down.
 *
 * Two decimals at most and no trailing zeroes, because these are readings: "78.84 kg", "79 kg",
 * not "78.8400000001 kg" and not "79.00 kg".
 */
export const amount = (value: number, unit: string | null): string => {
  const rounded = Math.round(value * 100) / 100;
  return `${rounded}${unit ? ` ${rounded === 1 ? singular(unit) : unit}` : ''}`;
};

/**
 * A period's figure against its target, as both hover texts state it: the row strip's cell and
 * the 12-week chart's bar.
 *
 * One function because they are two views of the same week, and they drifted. The strip went
 * through `amount()` in `fcf8ae7` while the chart's bar kept `${p.total}`, so a week logged as
 * 16.8 + 1.1 — a sum JavaScript makes 17.900000000000002 — read `17.9 t of 18` on the strip and
 * `17.900000000000002 t of 18` on the chart directly below it (`01a08094-6da4`).
 *
 * A reading is stated against the target, never divided by it; a ceiling says what was allowed.
 */
export const periodFigure = (
  total: number,
  target: number,
  unit: string | null,
  polarity: 'positive' | 'negative',
  reading: boolean,
): string =>
  reading
    ? `${amount(total, unit)}, target ${amount(target, unit)}`
    : `${amount(total, unit)} of ${amount(target, null)}${polarity === 'negative' ? ' allowed' : ''}`;

/**
 * The same rounding, applied to numbers already embedded in a sentence.
 *
 * `amount()` can only help a surface that still has the number. A notification body is a *stored
 * string*, written when the notification was sent — so teaching the writer to round (which Phase 31
 * did) fixes everything sent afterwards and nothing already in the bell. The health nut was still
 * reading "50.300000000000004 of 50 bpm allowed this period." across a 24-item backlog, and from
 * the reader's side "that row is a record of what was sent" is a distinction without a difference.
 *
 * Rounding at render fixes every historical row at once and cannot corrupt the stored record.
 *
 * Three or more decimal places, so a deliberate two-place figure is left exactly as written and a
 * date or a version number is never touched.
 */
export const roundNumbersIn = (text: string): string =>
  text.replace(/\d+\.\d{3,}/g, (n) => String(Math.round(Number(n) * 100) / 100));

/** The same, with an explicit sign — a change of zero is written as zero, not "+0". */
export const signedAmount = (value: number, unit: string | null): string => {
  const rounded = Math.round(value * 100) / 100;
  if (rounded === 0) return amount(0, unit);
  // A real minus sign, not a hyphen: these sit at 1.25rem next to a "+" and the hyphen is a
  // third of the width, so a column of changes visibly fails to line up.
  return rounded > 0 ? `+${amount(rounded, unit)}` : `−${amount(Math.abs(rounded), unit)}`;
};

/** The same rounding and the same real minus sign as `signedAmount`, for a percentage. */
const signedPercent = (ratio: number): string => {
  const pct = Math.round(ratio * 100);
  if (pct === 0) return '0%';
  return pct > 0 ? `+${pct}%` : `\u2212${Math.abs(pct)}%`;
};

/**
 * How far the two medians may differ and still be called *steady*, as a fraction.
 *
 * The browser's copy of `TREND_TOLERANCE` in `@planner/core`, restated for the same reason
 * `LIMIT_MARGIN` is — the bundle deliberately does not import core, and a constant this small is
 * cheaper than pulling Drizzle into the browser. Only the *relative* rule has a number the browser
 * can state: a measurement's threshold is two standard errors of the gap between the medians,
 * computed from the series itself, and there is nothing constant to copy.
 */
const TREND_TOLERANCE = 0.05;

/**
 * Which rule produced the word, so *steady* is a claim rather than a mood.
 *
 * "steady" was a bare word with nothing behind it, and there was no way to tell it from "we have
 * not looked". It means something exact and different for each basis, and the difference is worth
 * a sentence: a quantity you accumulate is judged against a flat fraction of the earlier median,
 * while a measurement is judged against how much a series this short wanders anyway — because a
 * percentage of a body weight is always small, and a flat 5% called a lost kilo and a quarter
 * steady.
 *
 * Provenance, so it stays a `title`: the numbers a reader checks the verdict against are in the
 * tile's `sub` where a thumb can reach them.
 */
function trendRule(trend: HabitTrend): string {
  // Nothing in the earlier half to be a percentage of. Neither threshold has a meaning here, so
  // the sentence does not pretend one applied — the two medians are the whole of the story.
  if (trend.basis === 'relative' && trend.changeRatio === null) {
    return trend.recent === trend.earlier
      ? 'Both medians are zero — there is nothing yet to compare.'
      : 'Up from nothing, which is a direction but not a percentage.';
  }
  const noise =
    trend.basis === 'absolute'
      ? 'the noise a series this short shows anyway'
      : `${Math.round(TREND_TOLERANCE * 100)}% of the earlier median`;
  return trend.verdict === 'steady'
    ? `Steady: the two medians differ by less than ${noise}.`
    : `A direction rather than noise: the two medians differ by more than ${noise}.`;
}

/** The word for a verdict. `unknown` gets none — the tile is not drawn at all without a trend. */
const TREND_WORD: Record<HabitTrend['verdict'], string> = {
  better: 'better',
  worse: 'worse',
  steady: 'steady',
  unknown: '',
};

/**
 * How to say which way a habit is going.
 *
 * Three things have to be said, and until Phase 56 the tile said only the third.
 *
 * **Better or worse**, which a signed number cannot carry. "-50%" over Alcohol (at most 2 units a
 * week) is the best news on the page; "-33%" over Strength session is the worst. Both rendered as
 * plain `rgb(28,28,26)` with no icon, arrow or tone, so reading the column meant recalling each
 * habit's polarity and applying it to a minus sign. The judgement itself is `trend.verdict` and is
 * made in the domain, so REST, MCP and this tile cannot disagree about it.
 *
 * **The word as well as the tone.** Colour is the reinforcement, never the message — the same
 * rule the status badges follow — and it is also what makes a column scannable: the tile used to
 * be a bare word ("steady") for some habits and a bare percentage for others, which is two
 * different tiles in one slot.
 *
 * **The size**, in the unit the habit deserves. A quantity you accumulate moved by a percentage —
 * "+19%" is the whole of what 14.8 → 20.4 tonnes a week means. A measurement moved by an amount
 * over a stretch of time, and its percentage is noise: 81.235 → 79.985 kg is "−1.5%", inside the
 * tolerance, and not what anyone weighing themselves wants to know.
 */
export function trendTile(
  trend: HabitTrend,
  unit: string | null,
  periodMany: string,
): { label: string; value: string; sub: string; hint: string; tone: string } {
  /*
    The two medians and the window, in the tile rather than in its `title`.

    This was the tooltip above, and a tooltip is where provenance goes — not where the thing that
    makes a number checkable goes; see `Tile`. The maximizer tried last-2-vs-previous-2,
    last-3/4/5/6 and simple halves over the eleven weekly bars drawn directly above this tile and
    got −100%, −82%, −58.5%, −44.3%, −32.2% and −16%, never the −38% the tile showed, because the
    statistic is a median of halves and nothing on the screen said so. Two numbers and a window
    turn it from a claim into an arithmetic anyone can repeat.

    The unit rides on the second number only. Spelled on both, "4 sessions → 0 sessions" wrapped
    inside the 7rem tile and left a line reading "4 sessions → 0", which is a worse sentence than
    the one it replaced.

    "completed" is not decoration. The tile sits beside a period pill that can read "too early to
    tell", and the two answer different questions: the pill is about the week in progress, and the
    trend never sees it — `computeTrend` drops `isCurrent` before it takes a median, deliberately,
    so a fabricated zero cannot reach one. Saying which periods were counted is what lets both
    sentences stand on one card, and is why the trend is not suppressed while the pill is unsure.
  */
  const sub = `${amount(trend.earlier, null)} → ${amount(trend.recent, unit)} · median of ${trend.halfLength} completed ${periodMany} against the ${trend.halfLength} before`;
  const tone = trend.verdict === 'better' ? 'ok' : trend.verdict === 'worse' ? 'bad' : '';
  const word = TREND_WORD[trend.verdict];
  const size =
    trend.basis === 'absolute'
      ? signedAmount(trend.changeAbsolute, unit)
      : trend.changeRatio !== null
        ? signedPercent(trend.changeRatio)
        : /*
            No earlier median to be a percentage of. Starting from nothing, the two numbers are the
            honest answer; nothing to nothing is 0%, and printing "0 → 0" would make the one steady
            tile that starts from zero look unlike every other steady tile.
          */
          trend.recent === trend.earlier
          ? '0%'
          : // Through `amount()` like the `sub` beneath it: a recent median of 16.8 + 1.1 weeks
            // printed *better 0 → 17.900000000000002* here (`01a15484-9eb4`'s sweep). No unit, for
            // the 7rem reason given above `sub`.
            `${amount(trend.earlier, null)} → ${amount(trend.recent, null)}`;
  return {
    // The span belongs to a measurement's change — "−1.25 kg over 34 days" — and there is no
    // room for it in a 7rem value, so it names the tile instead.
    label:
      trend.basis === 'absolute' && trend.spanDays > 0 ? `Over ${trend.spanDays} days` : 'Trend',
    value: word ? `${word} ${size}` : size,
    sub,
    hint: trendRule(trend),
    tone,
  };
}

/**
 * How far a measurement still has to go, said in one direction-free phrase.
 *
 * `toTarget` is signed so that positive always means "not there yet", whichever way the habit
 * runs — so this never has to branch on polarity, which is where the "Met every one of the last
 * 11 days" congratulation on a target missed every time came from.
 */
export const toTargetLabel = (toTarget: number, unit: string | null): string =>
  toTarget > 0 ? `${amount(toTarget, unit)} to go` : `${amount(-toTarget, unit)} past it`;

/**
 * The same fact split for a tile, where a whole phrase at 1.25rem wraps to three lines.
 *
 * The direction moves into the label rather than being dropped: which side of the target you are
 * on is the entire content of the tile.
 */
export const toTargetTile = (
  toTarget: number,
  unit: string | null,
): { label: string; value: string } => ({
  label: toTarget > 0 ? 'Still to go' : 'Past the target',
  value: amount(Math.abs(toTarget), unit),
});

export function HabitHistoryPanel({
  progress,
  timezone,
}: {
  progress: HabitProgress;
  /** The account's zone, passed straight through to `Backdate` — see the prop there. */
  timezone: string;
}) {
  const habit = progress.habit;
  const history = useQuery({
    queryKey: ['habit-history', habit.id],
    queryFn: () => getHabitHistory(habit.id),
  });

  const noun = HABIT_PERIOD_NOUN[habit.period] ?? HABIT_PERIOD_NOUN.week!;
  const unit = habit.unit;
  /*
    The one gate this whole panel turned on.

    `isMeasurement` has existed since Phase 31 and was called in exactly one place — the target
    suggestion — so every other tile carried on treating a body weight as a workload. "All time:
    5640.26 kg" is seventy mornings added up; "Days at target 0/69" is true and says the same
    thing on the morning before you reach the target as on the first day of trying; and the run
    counter is counting something nobody set out to do.
  */
  // Said by the service — see `HabitProgress.isReading`. This was `isMeasurement(habit.aggregation)`
  // against a browser copy of the flag, which is the third place the same question was answered.
  const measured = progress.isReading;
  const record = history.data?.measurement ?? null;
  // Counted over the periods the chart drew, so the history's runs rather than the row's.
  const run = history.data
    ? runFigure(
        { ...history.data, runWindow: history.data.periods.length, isReading: measured },
        habit.period,
      )
    : null;
  /* Which way it is going, which a met-rate cannot say — it cannot rise above what the target
     allows, so twelve weeks of real improvement read as one unchanging number. */
  const trend =
    history.data && history.data.trend.direction !== 'unknown'
      ? trendTile(history.data.trend, unit, noun.many)
      : null;

  return (
    <div className="habit-history">
      <div className="stat-row">
        {measured ? (
          record && (
            <>
              <Tile
                label="Now"
                value={amount(record.latest, unit)}
                hint={`Last read on ${shortDay(record.latestOn)}`}
                testId="tile-now"
              />
              <Tile
                label="Best"
                value={amount(record.best, unit)}
                hint={`Reached on ${shortDay(record.bestOn)}`}
                testId="tile-best"
              />
              <Tile
                label="Since the start"
                value={signedAmount(record.changeSinceStart, unit)}
                hint={`${amount(record.first, unit)} on ${shortDay(record.firstOn)}`}
                testId="tile-since-start"
              />
            </>
          )
        ) : (
          <Tile
            label={`Logged this ${noun.one}`}
            value={amount(progress.actual, unit)}
            testId="tile-logged"
          />
        )}
        {/*
          `expectedByNow` has been computed by the domain and serialized by the API since
          Phase 3 and was never shown. It is the difference between "2 of 3" and "2 of 3, and
          you should be at 2.6 by now" — the second is actionable.

          Not for a measurement: a weight does not accrue at an even pace across a week.
        */}
        {/*
          And nothing at all while the habit is paused: the server omits `expectedByNow` for a
          stood-down target, because a pace is a debt against something still in force. The tile
          used to read "Expected by now 0.7" under a card whose own chip said paused.
        */}
        {!progress.isAbstinence &&
          !measured &&
          habit.polarity === 'positive' &&
          progress.expectedByNow !== undefined && (
            <Tile
              label="Expected by now"
              value={progress.expectedByNow.toFixed(1)}
              hint="At an even pace across the period"
              testId="tile-expected"
            />
          )}
        {/* The distance, which replaces the met-rate: "0.84 kg to go" is the same fact as
            "0 of 69" and the only one of the two you can do anything with. */}
        {measured && record && (
          <Tile
            {...toTargetTile(record.toTarget, unit)}
            hint={`Target ${amount(habit.targetCount, unit)}`}
            testId="tile-to-target"
          />
        )}
        {measured && record && (
          <Tile
            label={habit.polarity === 'negative' ? 'Days under it' : 'Days above it'}
            value={`${record.daysAtTarget}/${record.daysMeasured}`}
            hint="Days measured on the target side of the line"
            /* The other fraction on this row, under the same rule: the denominator is the days
               there was a reading at all, which is not the days the chart draws. */
            sub={`of ${record.daysMeasured} days measured`}
            testId="tile-days-at-target"
          />
        )}
        {!measured && history.data && (
          <Tile
            label={`${noun.many.replace(/^./, (c) => c.toUpperCase())} at target`}
            value={
              history.data.periodsConsidered === 0
                ? '—'
                : `${history.data.periodsMet}/${history.data.periodsConsidered}`
            }
            /*
              What the denominator is, rather than a percentage of it.

              "71% of completed days" restates the fraction above it and says nothing about the 28.
              The chart beside the tile draws ninety days, so a reader who wants to know where 28
              came from has to work out that a habit created four weeks ago has only been alive for
              four weeks — and the tile is the only thing that could have told them.

              Not moved to the chart's window, which was the other branch and is worse: 20/90 is 22%
              and sixty-two of those days are before the habit existed. That is a verdict on a
              period nobody was trying in.
            */
            /* The percentage keeps the tooltip — it is provenance, and it was never wrong, only
               alone. The denominator moves into the tile. */
            hint={
              history.data.rate === null
                ? 'No completed period yet'
                : `${Math.round(history.data.rate * 100)}% of completed ${noun.many}`
            }
            sub={
              history.data.periodsConsidered === 0 ? undefined : metSub(history.data, habit.period)
            }
            testId="tile-met"
          />
        )}
        {/*
          The best run, unconditionally.

          This tile was hidden behind `currentRun > 0`, and the best run lived in its tooltip — so
          the one fact worth having after a bad month ("you have done this before, here is the
          proof") was unreachable at exactly the moment it mattered. It is a floor that can only
          go up, which is the opposite of the streak §5.3 was right to reject.
        */}
        {/*
          And the run first, at zero too: `0 · best 12`. At zero the value was `best 12`, the record
          promoted into the run's slot under a label that says *In a row* — so a run broken last
          month and one alive for twelve days read alike, two lines below a card saying the first
          had none (`01a03813-f7d8`). `runFigure` says it, as it does on the card and on Today.
        */}
        {run && history.data && history.data.bestRun > 0 && (
          <Tile
            label="In a row"
            value={run.tile}
            /*
              The window, on this tile too.

              Both runs are counted over the periods the chart drew and nothing said so, which is
              half of the same finding: "the rate, denominator and best-run are all computed over
              whatever the chart happened to draw, while All time is the one tile that is not". A
              best run is a record, and a record that silently forgets everything before the left
              edge is the one number on this row a reader would never think to doubt.
            */
            hint={runHint(history.data, history.data.periods.length, habit.period)}
            testId="tile-run"
          />
        )}
        {history.data && history.data.lifetimeTotal !== null && history.data.lifetimeTotal > 0 && (
          <Tile
            label="All time"
            value={amount(history.data.lifetimeTotal, unit)}
            hint="Everything logged since this habit started"
            testId="tile-lifetime"
          />
        )}
        {trend && <Tile {...trend} testId="tile-trend" />}
      </div>

      {history.data?.suggestion && (
        <TargetSuggestionRow
          habitId={habit.id}
          suggestion={history.data.suggestion}
          current={habit.targetCount}
          unit={unit}
          period={noun.one}
        />
      )}

      <h4>
        Last {history.data?.periods.length ?? 12} {noun.many}
      </h4>
      <QueryState query={history} />
      {history.data &&
        /*
          The chart, or an honest sentence — never both.

          With a few days of history this drew a blank rectangle, a dashed target line and a single
          6px sliver, which reads as a broken chart rather than as a new habit. The note was
          already here; it just sat *under* the empty chart instead of replacing it.
        */
        (history.data.periodsConsidered === 0 ? (
          <p className="subtext" data-testid="history-empty">
            Nothing has completed yet — there will be something to judge after this {noun.one}.
          </p>
        ) : (
          <PeriodBars
            periods={history.data.periods}
            target={habit.targetCount}
            unit={unit}
            periodName={noun.one}
            measurement={measured}
            polarity={habit.polarity}
          />
        ))}

      <h4>Entries this {noun.one}</h4>
      <Entries habitId={habit.id} from={progress.bounds.start} to={progress.bounds.end} />
      <Backdate
        habitId={habit.id}
        unit={unit}
        bounds={progress.bounds}
        periodName={noun.one}
        measurement={measured}
        typicalValue={progress.typicalValue}
        timezone={timezone}
      />
    </div>
  );
}
